import React, { useCallback, useMemo } from 'react';
import { CandleType } from '../../../../Detail/types';
import { TimeFrame } from 'grts-utils/types/TimeFrame';
import InputField from '../../../../../components/Form/InputField';
import { colors } from '../../../../../colors';
import DropdownComponent from '../../../../../components/Form/Dropdown';

export type RSIConfig = {
  period: string;
  threshold: string;
  candleType: CandleType;
  timeFrame: TimeFrame;
  childTimeFrame: TimeFrame;
};

export const defaultRsiConfig = {
  period: '5',
  threshold: '70',
  candleType: CandleType.Normal,
  timeFrame: TimeFrame.TwoDays,
  childTimeFrame: TimeFrame.TwoDays,
};

const RSIForm = ({
  label,
  rsiConfig,
  setRsiConfig,
}: {
  label?: string;
  rsiConfig: RSIConfig;
  setRsiConfig: React.Dispatch<React.SetStateAction<RSIConfig>>;
}) => {
  const updateValue = useCallback(
    (key: string, value: string) => {
      setRsiConfig((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [setRsiConfig],
  );

  const periodField = useMemo(() => {
    return {
      label: 'Period',
      value: rsiConfig.period,
      handleValueChange: (value: string) =>
        updateValue('period', value.slice(0, 2)),
      shouldFit: true,
    };
  }, [rsiConfig.period, updateValue]);

  const thresholdField = useMemo(() => {
    return {
      label: 'Threshold',
      value: rsiConfig.threshold,
      handleValueChange: (value: string) =>
        updateValue('threshold', value.slice(0, 3)),
      shouldFit: true,
    };
  }, [rsiConfig.threshold, updateValue]);

  const candleTypeField = useMemo(() => {
    return {
      label: 'Candle Type',
      value: rsiConfig.candleType,
      handleValueChange: (value: CandleType) =>
        updateValue('candleType', value),
      options: Object.values(CandleType),
    };
  }, [rsiConfig.candleType, updateValue]);

  const timeFrameField = useMemo(() => {
    return {
      label: 'TimeFrame',
      value: rsiConfig.timeFrame,
      handleValueChange: (value: TimeFrame) => updateValue('timeFrame', value),
      options: Object.values(TimeFrame),
    };
  }, [rsiConfig.timeFrame, updateValue]);

  const childTimeFrameField = useMemo(() => {
    return {
      label: 'Child TimeFrame',
      value: rsiConfig.childTimeFrame,
      handleValueChange: (value: TimeFrame) =>
        updateValue('childTimeFrame', value),
      options: Object.values(TimeFrame),
    };
  }, [rsiConfig.childTimeFrame, updateValue]);

  return (
    <div
      style={{
        marginLeft: 50,
        marginRight: 50,
        marginTop: 0,
        marginBottom: 10,
      }}
    >
      <p style={{ fontWeight: 'bold', color: colors.white }}>
        {label ?? 'RSI Information'}
      </p>
      <div style={{ display: 'flex', columnGap: 16 }}>
        <InputField config={periodField} />
        <InputField config={thresholdField} />
        <DropdownComponent config={candleTypeField} />
        <DropdownComponent config={timeFrameField} />
        <DropdownComponent config={childTimeFrameField} />
      </div>
    </div>
  );
};

export default RSIForm;

// - Add period/threshold/candletype/timeframe/childTimeframe section for the RSI
